import React from "react";
import styled from "styled-components";
import { FiCheckSquare } from "react-icons/fi";
import { colors } from "./colors";
import { formatDay } from "./format";
import { MONO } from "./Row";
import { SectionTitle, SectionNote } from "./Section";

export default function PageChangeList({ changes, trendStart }) {
    return (
        <section aria-labelledby="changes-heading">
            <SectionTitle id="changes-heading">
                Detected page changes
                <SectionNote>From daily crawl diff, including CMS edits</SectionNote>
            </SectionTitle>
            {changes.length === 0 ? (
                <None>
                    <FiCheckSquare aria-hidden />
                    <p>
                        <strong>No on-page changes since {formatDay(trendStart)}.</strong> Title, meta, canonical, robots, hreflang,
                        content and status code are unchanged, so the cause is likely external.
                    </p>
                </None>
            ) : (
                <List>
                    {changes.map((change) => (
                        <Change key={`${change.date}-${change.field}-${change.url}`}>
                            <Head>
                                <Field>{change.field}<Url>{change.url}</Url></Field>
                                <When>{formatDay(change.date)}{change.scope ? ` · ${change.scope}` : ""}</When>
                            </Head>
                            <Diff>
                                <Removed><dt aria-label="Before">−</dt><dd>{change.before}</dd></Removed>
                                <Added><dt aria-label="After">+</dt><dd>{change.after}</dd></Added>
                            </Diff>
                        </Change>
                    ))}
                </List>
            )}
        </section>
    );
}

const None = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 12px;
    border-radius: 8px;
    border: 1px dashed ${colors.lineStrong};
    padding: 12px 16px;
    svg { margin-top: 2px; flex-shrink: 0; color: ${colors.positive}; }
    p { margin: 0; font-size: 13px; color: ${colors.muted}; }
    strong { font-weight: 500; color: ${colors.ink}; }
`;

const List = styled.ol`
    margin: 12px 0 0;
    padding: 0;
    list-style: none;
`;

const Change = styled.li`
    overflow: hidden;
    margin-top: 12px;
    border-radius: 8px;
    border: 1px solid ${colors.line};
    &:first-child { margin-top: 0; }
`;

const Head = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 2px 12px;
    padding: 8px 12px;
    background: ${colors.canvas}99;
`;

const Field = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.ink};
`;

const Url = styled.span`
    margin-left: 8px;
    font-family: ${MONO};
    font-size: 12px;
    font-weight: 400;
    color: ${colors.muted};
`;

const When = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${colors.muted};
`;

const Diff = styled.dl`
    margin: 0;
    font-family: ${MONO};
    font-size: 12px;
    dt { width: 12px; flex-shrink: 0; }
    dd { margin: 0; word-break: break-word; }
`;

const Removed = styled.div`
    display: flex;
    gap: 8px;
    padding: 6px 12px;
    background: ${colors.criticalSoft}99;
    color: ${colors.criticalInk};
`;

const Added = styled.div`
    display: flex;
    gap: 8px;
    padding: 6px 12px;
    border-top: 1px solid ${colors.line};
    background: ${colors.positiveSoft}99;
    color: ${colors.positiveInk};
`;
